import { JSX, useState, useRef, useEffect, SyntheticEvent } from 'react';
import classNames from 'classnames';
import { RippleButton } from '@bg-components/RippleButton';
import { Tooltip } from '@bg-components/Tooltip';
import { DirectionalIcon, IconDirection } from '@bg-components/Icon';
import { LobbyModal } from '@bg-components/Modals/LobbyModal';
import { storageRepository } from '@betgames/bg-tools';
import { t, useModalsManager, ModalName, LOBBY_CLICKED_KEY, useAnyBetsPlaced } from '@bg-shared';
import classes from './LobbyButton.module.scss';

const LOBBY_TOOLTIP_DURATION = 3000;

export const LobbyButton = (): JSX.Element => {
    const [openDropdown, setOpenDropdown] = useState<boolean>(false);
    const [shine, setShine] = useState<boolean>(
        !Number(storageRepository.getItem(LOBBY_CLICKED_KEY)),
    );
    const modalManager = useModalsManager();
    const shineIntervalRef = useRef<number>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [showTooltip, setShowTooltip] = useState<boolean>(false);
    const { currentGameHasBets } = useAnyBetsPlaced();

    const clearShine = () => {
        setShine(false);
        storageRepository.setItem(LOBBY_CLICKED_KEY, '1');
        clearInterval(shineIntervalRef.current);
    };

    const handleClick = (event: SyntheticEvent) => {
        if (currentGameHasBets) {
            event.preventDefault();
            setShowTooltip(true);
            return;
        }

        if (!openDropdown) {
            modalManager.open(ModalName.LOBBY, LobbyModal, {
                lobbyButtonRef: buttonRef,
                onClose: () => {
                    setOpenDropdown(false);
                },
                setOpenDropdown,
            });
            clearShine();
        } else {
            modalManager.close(ModalName.LOBBY);
        }
        setOpenDropdown(!openDropdown);
    };

    useEffect(() => {
        if (!shine) {
            return;
        }

        shineIntervalRef.current = setInterval(() => {
            setShine((value) => !value);
        }, Number(classes.shineDuration));
    }, []);

    return (
        <div className={classes.wrapper}>
            <RippleButton
                dataQa="lobby-button"
                className={classNames(classes.container, {
                    [classes.active]: openDropdown,
                    [classes.shine]: shine,
                    [classes.disabled]: currentGameHasBets,
                })}
                onClick={handleClick}
                ref={buttonRef}
            >
                <DirectionalIcon
                    className={classes.icon}
                    direction={openDropdown ? IconDirection.RIGHT : IconDirection.LEFT}
                />
                {t.string('games')}
            </RippleButton>
            <Tooltip
                className={classes.tooltip}
                duration={LOBBY_TOOLTIP_DURATION}
                anchorSelect="[data-qa='lobby-button']"
                place="bottom"
                isOpen={showTooltip}
                onTimeout={() => setShowTooltip(false)}
            >
                {t.string('active_bet_is_settled')}
            </Tooltip>
        </div>
    );
};
